import { t as localeText } from "@/shared/lib/translate";
import { createPortal } from "react-dom";
import { CloseIcon, CheckIcon, WarningIcon, InfoIcon } from "./icons";
import {
  createContext,
  type ReactNode,
  type ComponentPropsWithoutRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { cn } from "@/shared/lib/cn";
import { IconButton } from "./IconButton";
import { InlineAction } from "./Actions";

export type ToastTone =
  "neutral" | "success" | "warning" | "danger" | "progress";

export interface ToastOptions {
  tone?: ToastTone;
  description?: string;
  /** Optional decorative preview of the asset mentioned in the message. */
  previewSrc?: string;
  /** A determinate percentage; omit for the existing spinner-only treatment. */
  progress?: number;
  actionLabel?: string;
  onAction?: () => void;
  persistent?: boolean;
  dismissLabel?: string;
}

interface ToastState extends ToastOptions {
  id: number;
  message: string;
  leaving: boolean;
}

interface ToastContextValue {
  toast: (message: string, options?: ToastOptions) => void;
}

interface ToastAnnouncements {
  polite: ToastState | null;
  assertive: ToastState | null;
}

const ToastContext = createContext<ToastContextValue | null>(null);
const VISIBLE_MS = 6000;
const EXIT_MS = 180;

/** Polite floating notifications. Actionable and error toasts persist. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastState[]>([]);
  const [announcements, setAnnouncements] = useState<ToastAnnouncements>({
    polite: null,
    assertive: null,
  });
  const idRef = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>[]>());

  const clearToastTimers = useCallback((id: number) => {
    timers.current.get(id)?.forEach(clearTimeout);
    timers.current.delete(id);
  }, []);

  const remove = useCallback(
    (id: number) => {
      clearToastTimers(id);
      setToasts((items) => items.filter((item) => item.id !== id));
    },
    [clearToastTimers],
  );

  const dismiss = useCallback(
    (id: number) => {
      clearToastTimers(id);
      setToasts((items) =>
        items.map((item) =>
          item.id === id ? { ...item, leaving: true } : item,
        ),
      );
      timers.current.set(id, [setTimeout(() => remove(id), EXIT_MS)]);
    },
    [clearToastTimers, remove],
  );

  const schedule = useCallback(
    (item: ToastState) => {
      if (
        item.leaving ||
        item.persistent ||
        item.actionLabel ||
        item.tone === "danger" ||
        item.tone === "progress"
      )
        return;
      clearToastTimers(item.id);
      timers.current.set(item.id, [
        setTimeout(() => dismiss(item.id), VISIBLE_MS),
      ]);
    },
    [clearToastTimers, dismiss],
  );

  useEffect(
    () => () => {
      timers.current.forEach((group) => group.forEach(clearTimeout));
      timers.current.clear();
    },
    [],
  );

  const toast = useCallback(
    (message: string, options: ToastOptions = {}) => {
      const item: ToastState = {
        id: ++idRef.current,
        message,
        tone: options.tone ?? "neutral",
        leaving: false,
        ...options,
      };
      setToasts((items) => [...items.slice(-2), item]);
      setAnnouncements((current) =>
        item.tone === "danger"
          ? { ...current, assertive: item }
          : { ...current, polite: item },
      );
      schedule(item);
    },
    [schedule],
  );

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {createPortal(<div data-afterform-notifications="">
      <div role="status" aria-atomic="true" className="sr-only">
        {announcements.polite ? (
          <span key={announcements.polite.id}>
            {announcements.polite.message}{announcements.polite.description ? ` — ${announcements.polite.description}` : ""}
          </span>
        ) : null}
      </div>
      <div role="alert" aria-atomic="true" className="sr-only">
        {announcements.assertive ? (
          <span key={announcements.assertive.id}>
            {announcements.assertive.message}{announcements.assertive.description ? ` — ${announcements.assertive.description}` : ""}
          </span>
        ) : null}
      </div>
      <div className="pointer-events-none fixed inset-x-4 bottom-4 z-[100] flex flex-col items-end gap-2 sm:start-auto sm:w-[372px]">
        {toasts.map((item) => (
          <ToastCard
            key={item.id}
            message={item.message}
            tone={item.tone}
            description={item.description}
            previewSrc={item.previewSrc}
            progress={item.progress}
            actionLabel={item.actionLabel}
            onAction={item.onAction ? () => { item.onAction?.(); dismiss(item.id); } : undefined}
            dismissLabel={item.dismissLabel}
            onDismiss={() => dismiss(item.id)}
            className={cn(
              item.leaving
                ? "motion-safe:[animation:toast-fall_180ms_var(--ease-house)_forwards]"
                : "motion-safe:[animation:toast-rise_200ms_var(--ease-house)]",
            )}
            onPointerEnter={() => clearToastTimers(item.id)}
            onPointerLeave={() => schedule(item)}
            onFocus={() => clearToastTimers(item.id)}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget))
                schedule(item);
            }}
          />
        ))}
      </div>
      </div>, document.body)}
    </ToastContext.Provider>
  );
}

export interface ToastCardProps extends ComponentPropsWithoutRef<"div"> {
  message: string;
  tone?: ToastTone;
  description?: string;
  previewSrc?: string;
  progress?: number;
  actionLabel?: string;
  onAction?: () => void;
  dismissLabel?: string;
  onDismiss: () => void;
}

/** The same frosted notification surface in the live stack and design previews. */
export function ToastCard({ message, tone = "neutral", description, previewSrc, progress, actionLabel, onAction, dismissLabel = "Dismiss notification", onDismiss, className, ...props }: ToastCardProps) {
  const percentage = progress === undefined || !Number.isFinite(progress) ? undefined : Math.min(100, Math.max(0, progress));
  return <div data-progress={percentage !== undefined || undefined} className={cn("studio-toast pointer-events-auto flex w-[372px] max-w-full items-center gap-3 studio-overlay rounded-lg px-3.5 py-3 font-sans text-[13px] text-ink", className)} {...props}>
    {previewSrc ? <img src={previewSrc} alt="" width={40} height={48} className="h-12 w-10 shrink-0 rounded-sm object-cover" /> : <ToastMark tone={tone} />}
    <div className="min-w-0 flex-1 break-words leading-[1.5]">
      <div className={percentage !== undefined ? "studio-toast-progress-heading" : undefined}>
        <span className={description ? "font-medium" : undefined} title={percentage !== undefined ? message : undefined}>{message}</span>
        {percentage !== undefined && description ? <span className="studio-toast-progress-meta">{description}</span> : null}
      </div>
      {percentage === undefined && description ? <p className="mt-0.5 text-xs text-ink-muted">{description}</p> : null}
      {percentage !== undefined ? <div role="progressbar" aria-label={message} aria-valuemin={0} aria-valuemax={100} aria-valuenow={percentage} className="mt-2 h-1 rounded-pill bg-[var(--progress-track)]"><div className="h-full rounded-pill bg-[var(--badge-video)]" style={{width: `${percentage}%`}} /></div> : null}
    </div>
    {actionLabel && onAction ? <InlineAction className="studio-toast-action shrink-0 text-xs" onClick={onAction}>{actionLabel}</InlineAction> : null}
    <IconButton size="sm" aria-label={dismissLabel} className="text-ink-muted" onClick={onDismiss}><CloseIcon /></IconButton>
  </div>;
}

function ToastMark({ tone }: { tone: ToastTone }) {
  if (tone === "progress") {
    return (
      <span
        aria-hidden="true"
        className="grid size-7 shrink-0 place-items-center rounded-sm bg-media-video"
      >
        <span className="size-[14px] animate-spin rounded-full border-2 border-white/35 border-t-white motion-reduce:animate-none" />
      </span>
    );
  }
  const Mark = tone === "success" ? CheckIcon : tone === "warning" ? WarningIcon : tone === "danger" ? CloseIcon : InfoIcon;
  return (
    <span
      aria-hidden="true"
      className={cn(
        "grid size-7 shrink-0 place-items-center rounded-sm text-xs font-semibold text-white",
        tone === "success" && "bg-success",
        tone === "warning" && "bg-warning text-[var(--warning-on)]",
        tone === "danger" && "bg-danger",
        tone === "neutral" && "bg-action-bg",
      )}
    >
      <Mark className="size-4 shrink-0" />
    </span>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error(localeText("interface.usetoast_must_be_used_within_toastprovider_0a41b3a8", { defaultValue: "useToast must be used within ToastProvider" }));
  return ctx;
}
